import React, { useEffect, useRef } from "react"
import { EditorContent, Editor } from "@tiptap/react"
import { Button } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faShuffle,
  faWandMagicSparkles,
  faTriangleExclamation,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons"
import Output from "./Output.component"
import Split from "react-split"
import { EngineQueryResult } from "types/engineOutput.types"
import { useQueryConsoleLayoutStore } from "stores/queryConsoleLayoutStore"

// Define the props interface for the Console component
interface ConsoleProps {
  /** The Tiptap editor instance */
  editor: Editor | null
  /** The result of the script execution */
  result: EngineQueryResult
  /** Indicates whether a script is currently running */
  scriptInProgress: boolean
  /** Indicates whether an async script is currently running */
  asyncScriptInProgress: boolean
  /** The sizes of the split view panes */
  splitSizes: number[]
  /** Function to handle split size changes */
  onSplitChange: (sizes: number[]) => void
  /** Orientation of the editor/output split */
  direction?: "vertical" | "horizontal"
  /** Whether the editor or the output appears first */
  order?: "console-first" | "output-first"
  /** Container-supplied callback to leave Console-as-viewer for the structured
   *  record's native tab. Needs to clear editor content + selectedQuery so the
   *  user doesn't return to a half-loaded script with stale state. */
  onOpenStructuredInTab?: (kind: "pipeline" | "builder") => void
}

// Define the structure for line height calculations
interface LineHeight {
  height: number
  top: number
}

// Define the structure for visible line information
interface VisibleLine {
  index: number
  top: number
  height: number
}

/**
 * Console component for writing and running scripts.
 *
 * This component provides a split view with an editor for writing scripts
 * and an output area for displaying results. It includes virtualized line numbers
 * for the editor and allows resizing of the editor and output areas.
 *
 * @component
 * @param props - The component props.
 * @returns The rendered Console component.
 */
const Console: React.FC<ConsoleProps> = ({
  editor,
  result,
  scriptInProgress,
  asyncScriptInProgress,
  splitSizes,
  onSplitChange,
  direction = "vertical",
  order = "console-first",
  onOpenStructuredInTab,
}) => {
  const editorWrapperRef = useRef<HTMLDivElement>(null)
  const lineNumbersRef = useRef<HTMLDivElement>(null)
  const structuredOrigin = useQueryConsoleLayoutStore(
    (s) => s.consoleStructuredOrigin
  )
  const structuredEditable = useQueryConsoleLayoutStore(
    (s) => s.consoleStructuredEditable
  )
  const setStructuredEditable = useQueryConsoleLayoutStore(
    (s) => s.setConsoleStructuredEditable
  )

  // Apply the structured-record read-only mode to the editor. When the user
  // clicks "Edit anyway" we flip back to editable; clearing the origin (e.g.
  // creating a new script) returns the editor to its default editable state.
  useEffect(() => {
    if (!editor) return
    const shouldBeEditable = !structuredOrigin || structuredEditable
    if (editor.isEditable !== shouldBeEditable) {
      editor.setEditable(shouldBeEditable)
    }
  }, [editor, structuredOrigin, structuredEditable])

  /**
   * Updates the line numbers in the editor.
   * This effect runs when the editor instance changes or updates.
   */
  useEffect(() => {
    if (!editor) return

    const lineNumbersContainer = lineNumbersRef.current
    if (!lineNumbersContainer) return

    const editorScrollContainer = editorWrapperRef.current
    if (!editorScrollContainer) return

    // Function to render virtualized line numbers
    const renderLineNumbers = (): void => {
      const { scrollTop, clientHeight } = editorScrollContainer
      const scrollBottom = scrollTop + clientHeight

      const lines = editor.view.dom.querySelectorAll(".ProseMirror > *")
      const lineHeights: LineHeight[] = []
      let totalHeight = 0

      // Calculate cumulative heights to determine line positions
      lines.forEach((line) => {
        const element = line as HTMLElement
        const height = element.offsetHeight
        lineHeights.push({ height, top: totalHeight })
        totalHeight += height
      })

      // Find the visible lines
      const visibleLines: VisibleLine[] = []
      for (let i = 0; i < lineHeights.length; i++) {
        const { top, height } = lineHeights[i]
        if (top + height >= scrollTop && top <= scrollBottom) {
          visibleLines.push({ index: i, top, height })
        }
        if (top > scrollBottom) {
          break
        }
      }

      // Clear existing line numbers
      lineNumbersContainer.innerHTML = ""

      // Render visible line numbers
      visibleLines.forEach(({ index, top, height }) => {
        const lineNumber = document.createElement("div")
        lineNumber.className = "line-number"
        lineNumber.textContent = (index + 1).toString()
        lineNumber.style.top = `${top}px`
        lineNumber.style.height = `${height}px`
        lineNumbersContainer.appendChild(lineNumber)
      })
    }

    // Initial render
    renderLineNumbers()

    // Scroll event listener with throttling
    let scrollTimeout: NodeJS.Timeout | null = null
    const handleScroll = (): void => {
      if (scrollTimeout) return
      scrollTimeout = setTimeout(() => {
        renderLineNumbers()
        scrollTimeout = null
      }, 50) // Adjust the throttle delay as needed
    }

    editorScrollContainer.addEventListener("scroll", handleScroll)

    // Update line numbers on editor updates
    editor.on("update", renderLineNumbers)

    // Clean up
    return () => {
      editorScrollContainer.removeEventListener("scroll", handleScroll)
      editor.off("update", renderLineNumbers)
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [editor])

  const banner = structuredOrigin ? (
    <StructuredBanner
      origin={structuredOrigin}
      editable={structuredEditable}
      onEditAnyway={() => setStructuredEditable(true)}
      onOpenInTab={() => {
        // Container clears editor + selectedQuery + origin in one go and
        // switches the view. Doing it here would leave Console with stale
        // state when the user comes back.
        onOpenStructuredInTab?.(structuredOrigin.kind)
      }}
    />
  ) : null

  const editorPane = (
    <div className="h-full flex flex-col min-h-0 relative">
      {banner}
      <div
        ref={editorWrapperRef}
        className="flex relative overflow-auto flex-grow"
      >
        {/*
          Line Numbers Container
          - Positioned absolutely to align with the editor content
        */}
        <div
          ref={lineNumbersRef}
          className="line-numbers border-gray-300"
          style={{ position: "absolute", left: 0, top: 0 }}
        ></div>
        <div className="w-full">
          <EditorContent editor={editor} className="editor-content w-full" />
        </div>
      </div>
    </div>
  )

  const outputPane = (
    <div className="h-full overflow-hidden">
      <Output
        result={result}
        scriptInProgress={scriptInProgress}
        asyncScriptInProgress={asyncScriptInProgress}
      />
    </div>
  )

  const containerClass =
    direction === "vertical"
      ? "split-container h-full flex flex-col"
      : "split-container h-full flex flex-row"

  const panes =
    order === "console-first"
      ? [editorPane, outputPane]
      : [outputPane, editorPane]

  return (
    /*
      key forces react-split to remount when direction/order changes.
      Without this it keeps stale inline width/height styles from the
      previous orientation, leaving panes the wrong size and the gutter
      unreachable.
    */
    <Split
      key={`${direction}-${order}`}
      direction={direction}
      sizes={splitSizes}
      onDragEnd={onSplitChange}
      minSize={100}
      expandToMin={false}
      gutterSize={6}
      gutterAlign="center"
      snapOffset={30}
      dragInterval={1}
      className={containerClass}
    >
      {panes[0]}
      {panes[1]}
    </Split>
  )
}

interface StructuredBannerProps {
  origin: { name: string; kind: "pipeline" | "builder" }
  editable: boolean
  onEditAnyway: () => void
  onOpenInTab: () => void
}

/**
 * Read-only-by-default banner shown above the Console editor when the editor
 * is displaying the script behind a saved pipeline or builder. The two
 * primary actions ("Open in Transform/Builder" and "Edit anyway") give the
 * user an explicit choice between editing the structured record (which the
 * Transform/Builder UI can do safely) and forking it as a new script.
 */
const StructuredBanner: React.FC<StructuredBannerProps> = ({
  origin,
  editable,
  onEditAnyway,
  onOpenInTab,
}) => {
  const kindLabel = origin.kind === "pipeline" ? "pipeline" : "builder"
  const tabLabel = origin.kind === "pipeline" ? "Transform" : "Builder"
  const kindIcon = origin.kind === "pipeline" ? faShuffle : faWandMagicSparkles
  return (
    <div
      className="qc-console-banner"
      data-state={editable ? "editing" : "viewing"}
      role="status"
    >
      <span className="qc-console-banner__icon" aria-hidden>
        <FontAwesomeIcon icon={editable ? faTriangleExclamation : kindIcon} />
      </span>
      <div className="qc-console-banner__body">
        <p className="qc-console-banner__title">
          {editable ? (
            <>
              Editing as a new script. Saving here will{" "}
              <strong>not</strong> change the {kindLabel}{" "}
              <span className="qc-mono">{origin.name}</span>; you&rsquo;ll
              be prompted for a new name.
            </>
          ) : (
            <>
              Viewing the script behind the {kindLabel}{" "}
              <span className="qc-mono">{origin.name}</span>. Read-only —
              edits to {kindLabel} structure happen in {tabLabel}.
            </>
          )}
        </p>
      </div>
      <div className="qc-console-banner__actions">
        <Button onClick={onOpenInTab}>Open in {tabLabel}</Button>
        {!editable && (
          <Button
            icon={<FontAwesomeIcon icon={faPenToSquare} />}
            onClick={onEditAnyway}
          >
            Edit as new script
          </Button>
        )}
      </div>
    </div>
  )
}

export default Console
